import * as React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";

import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { Inquiry, InquiryMessage } from "../../api/inquiries";
import { setSnackbarMessage } from "../../actions/store/snackbar-actions";
import { SnackbarVariant } from "../../actions/store/type";
import { nl2br } from "../../util/general-utility";
import { formatDate, format } from "../../util/date-utility";
import { Capabilities } from "../../integration/capabilities";
import { MessageSignature } from "../../api/settings";
import { isIPhoneX } from '../../util/device-util';

interface State {
	message: string;
	messageHistory: InquiryMessage[];
	recipientName: string;
	templateAnchorElement: HTMLElement;
}

export interface SendMessageDialogProps {
	open: boolean;
	inquiry: Inquiry;
	onCancel: () => void;
	onSend: (message: string) => void;
}

interface ConnectedSendMessageDialogProps extends WithStyles<typeof styles> {
	capabilities: Capabilities;
	signatures: MessageSignature[];
	setSnackbarMessage: (message: string, variant: SnackbarVariant) => void;
}

type Props = SendMessageDialogProps &
	ConnectedSendMessageDialogProps &
	InjectedTranslateProps;

export class SendMessageDialog extends React.Component<Props, State> {
	public state: State = {
		message: "",
		messageHistory: [],
		recipientName: "",
		templateAnchorElement: null
	};

	public componentDidMount() {
		this.setStateFromProps(this.props);
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (nextProps.open) {
			this.setStateFromProps(nextProps);
		}
	}

	private setStateFromProps = (props: Props) => {
		this.setState({
			message: "",
			messageHistory: props.inquiry ? props.inquiry.messages : [],
			recipientName: props.inquiry ? props.inquiry.primaryName : ""
		});
	};

	private handleMessageChange = (
		event: React.ChangeEvent<HTMLTextAreaElement>
	) => {
		this.setState({
			message: event.currentTarget.value
		});
	};

	private handleCancelClick = () => {
		this.props.onCancel();
	};

	private handleSendClick = () => {
		const { message } = this.state;

		if (message && message !== "") {
			this.props.onSend(message);
		} else {
			this.props.setSnackbarMessage(
				"Nachricht darf nicht leer sein!",
				"warning"
			);
		}
	};

	private handleTemplateButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		this.setState({
			templateAnchorElement: event.currentTarget
		});
	};

	private handleTemplateMenuClose = () => {
		this.setState({
			templateAnchorElement: null
		});
	};

	private replaceTemplateTags = (template: string) => {
		const { recipientName } = this.state;

		template = template.replace(/##NAME##/g, recipientName);

		return template;
	};

	private handleTemplateMenuItemClick = (signature: MessageSignature) => {
		this.setState({
			message: this.replaceTemplateTags(signature.content)
		});

		this.handleTemplateMenuClose();
	};

	private printMessageFooter = (message: InquiryMessage) => {
		const date = new Date(message.date);

		return formatDate(date) + " um " + format(date, "HH:mm");
	};

	private messageTagsToHTML  = (message: string) => {
		if (message.length < 1) return message;

		var linkTags = message.match(/(\[link=)((http|mailto:|tel:)(.*?))(\])/g)
		if (linkTags) {
			linkTags.forEach(function(placeholder){
				message = message.replace(placeholder, "<a href='" + placeholder.substring(6,placeholder.length - 1) + "'>")
			})
		}

		message = message.replace(/(\[\/link\])/g, "</a>")
		message = message.replace(/(\[b\])/g, "<b>")
		message = message.replace(/(\[\/b\])/g, "</b>")
		return message;
	};

	private renderMessageHistory() {
		const { classes } = this.props;
		const { messageHistory } = this.state;

		if (messageHistory.length > 0) {
			return (
				<div className={classes.messageHistory}>
					{messageHistory.map(entry => (
						<div
							key={entry.date}
							className={classNames(
								classes.messageHistoryEntry,
								classes.messageHistoryEntryEmployee
							)}
						>
							<div
								className={classNames(
									classes.messageHistoryEntryInner,
									classes.messageHistoryEntryInnerEmployee
								)}
							>
								<Typography
									className={classes.messageHistoryText}
									dangerouslySetInnerHTML={{
										__html: nl2br(this.messageTagsToHTML(entry.message))
									}}
								/>
								<div className={classes.messageHistoryFooter}>
									<Typography
										component="span"
										className={
											classes.messageHistoryFooterText
										}
									>
										{this.printMessageFooter(entry)}
									</Typography>
								</div>
							</div>
						</div>
					))}
				</div>
			);
		} else {
			return (
				<div className={classes.messageHistoryEmptyContainer}>
					<Typography className={classes.messageHistoryEmptyText}>
						Noch keine Nachrichten vorhanden!
					</Typography>
				</div>
			);
		}
	}

	private scrollDialogContentToBottom = () => {
		const content = document.querySelector(
			"." + this.props.classes.dialogContent
		);

		if (!content) return;

		const sh = content.scrollHeight;
		const ch = content.clientHeight;

		if (sh > ch) {
			content.scrollTo(0, sh - ch);
		}
	};

	public render() {
		const { classes, open, capabilities, signatures } = this.props;
		const { message, recipientName, templateAnchorElement } = this.state;

		return (
			<Dialog
				open={open}
				fullWidth
				fullScreen
			>
				<DialogTitle>Nachricht an {recipientName}:</DialogTitle>
				<DialogContent className={classes.dialogContent}>
					{this.renderMessageHistory()}
				</DialogContent>
				<DialogActions>
					<div
						ref={this.scrollDialogContentToBottom}
						className={classes.dialogActionContainer}
					>
						<div className={classes.dialogActionContent}>
							<TextField
								className={classes.textfield}
								placeholder="Nachricht eingeben..."
								rows={3}
								rowsMax={10}
								multiline
								value={message}
								onChange={this.handleMessageChange}
							/>
						</div>
						<div className={classes.dialogActionActions}>
							{signatures.length > 0 && (
								<React.Fragment>
									<Button
										className={classes.templateButton}
										onClick={this.handleTemplateButtonClick}
									>
										Vorlagen
									</Button>
									<Menu
										anchorEl={templateAnchorElement}
										open={Boolean(templateAnchorElement)}
										onClose={this.handleTemplateMenuClose}
									>
										{signatures.map(signature => (
											<MenuItem
												key={signature.id}
												onClick={() =>
													this.handleTemplateMenuItemClick(
														signature
													)
												}
											>
												{signature.name}
											</MenuItem>
										))}
									</Menu>
								</React.Fragment>
							)}
							<div style={{ flexGrow: 1 }} />
							<Button
								className={classes.cancelButton}
								onClick={this.handleCancelClick}
							>
								Abbrechen
							</Button>
							<Button
								className={classes.sendButton}
								onClick={this.handleSendClick}
								color="primary"
							>
								Senden
							</Button>
						</div>
					</div>
				</DialogActions>
			</Dialog>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	capabilities: state.capabilities,
	signatures: state.settings.signatures
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setSnackbarMessage: (message: string, variant: SnackbarVariant) =>
		dispatch(setSnackbarMessage(message, variant))
});

const styles = (theme: Theme) =>
	createStyles({
		textfield: {
			width: "100%",
			//height: "100px",
			padding: 10,

			"& div::before": {
				display: "none"
			},
			"& div::after": {
				display: "none"
			}
		},
		dialogContent: {
			background: "#f2f2f2"
		},
		dialogActionContainer: {
			flexGrow: 1
		},
		dialogActionContent: {},
		dialogActionActions: {
			display: "flex",
			justifyContent: "flex-end",
			paddingTop: 8,

			"&::after": {
				content: "''",
				position: "absolute",
				left: "0",
				width: "100%",
				height: 1,
				background: "#CCCCCC",
				marginTop: -8
			}
		},
		messageHistory: {
			display: "flex",
			flexDirection: "column"
		},
		messageHistoryEntry: {
			marginTop: 15,
			position: "relative",

			"&::after": {
				content: "''",
				display: "block",
				width: 16,
				height: 16,
				position: "absolute",
				bottom: 0
			}
		},
		messageHistoryEntryCustomer: {
			alignSelf: "flex-start",
			paddingRight: "15%",
			textAlign: "left",
			filter: "drop-shadow(0 0 1px #999999)",

			"&::after": {
				background: "white",
				left: 0,
				transform: "translateX(-100%) skew(-50deg)",
				transformOrigin: "bottom left"
			}
		},
		messageHistoryEntryEmployee: {
			alignSelf: "flex-end",
			paddingLeft: "15%",
			textAlign: "right",
			filter: "drop-shadow(0 0 1px #74e44f)",

			"&::after": {
				background: "#EBFFCA",
				right: 0,
				transform: "translateX(100%) skew(50deg)",
				transformOrigin: "bottom right"
			}
		},
		messageHistoryEntryInner: {
			padding: 10
		},
		messageHistoryEntryInnerCustomer: {
			background: "white"
		},
		messageHistoryEntryInnerEmployee: {
			background: "#EBFFCA"
		},
		messageHistoryText: {
			textAlign: "left"
		},
		messageHistoryFooter: {
			position: "relative",
			marginTop: 5,
			zIndex: 5,
			transform: "translateY(5px)"
		},
		messageHistoryFooterText: {
			fontSize: "0.8rem",
			fontStyle: "italic",
			color: "#888888"
		},
		messageHistoryEmptyContainer: {
			paddingTop: 24
		},
		messageHistoryEmptyText: {
			color: "#BBBBBB"
		},
		templateButton: {},
		cancelButton: {},
		sendButton: {}
	});

export const ConnectedSendMessageDialog = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(SendMessageDialog)
	)
);
